(() => {
  if (customElements.get('custom-menu-202610')) return;

  const desktopQuery = window.matchMedia('(min-width: 1311px)');
  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const focusableSelector = 'a[href], button:not([disabled]), input, select, textarea, [tabindex="0"]';
  const visibleFocusables = (root) => Array.from(root.querySelectorAll(focusableSelector))
    .filter((element) => !element.closest('[hidden], [inert]') && element.getClientRects().length);
  let openDrawer = null;
  let returnFocus = null;
  let previousOverflow = '';
  let hiddenWidgets = [];

  function closeDrawer(restoreFocus = true) {
    if (!openDrawer) return;
    const drawer = openDrawer;
    openDrawer = null;
    drawer.setAttribute('aria-hidden', 'true');
    drawer.inert = true;
    drawer.querySelector('custom-menu-202610')?.reset();
    document.body.style.overflow = previousOverflow;
    document.querySelectorAll('[data-cm26-open]').forEach((button) => button.setAttribute('aria-expanded', 'false'));
    hiddenWidgets.forEach(({ element, visibility }) => { element.style.visibility = visibility; });
    hiddenWidgets = [];
    if (restoreFocus && returnFocus?.isConnected) returnFocus.focus();
    returnFocus = null;
  }

  function showDrawer(button) {
    if (desktopQuery.matches) return;
    const id = button?.getAttribute('aria-controls');
    const drawer = (id && document.getElementById(id)) || document.querySelector('[data-cm26-drawer]');
    if (!drawer || openDrawer === drawer) return;
    closeDrawer(false);
    openDrawer = drawer;
    returnFocus = button || document.activeElement;
    previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    hiddenWidgets = ['launcher', 'geoip-banner'].map((widgetId) => document.getElementById(widgetId)).filter(Boolean)
      .map((element) => ({ element, visibility: element.style.visibility }));
    hiddenWidgets.forEach(({ element }) => { element.style.visibility = 'hidden'; });
    drawer.inert = false;
    drawer.setAttribute('aria-hidden', 'false');
    drawer.scrollTop = 0;
    button?.setAttribute('aria-expanded', 'true');
    drawer.querySelector('[data-cm26-close]')?.focus();
  }

  class CustomMenu202610 extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.mobile = this.dataset.mode === 'mobile';
      this.activeItem = null;
      this.screenStack = [];
      this.addEventListener('click', (event) => this.onClick(event), options);
      this.addEventListener('keydown', (event) => this.onKeyDown(event), options);
      if (!this.mobile) {
        this.addEventListener('pointerdown', (event) => {
          const trigger = event.target.closest('[data-cm26-trigger]');
          this.pointerTrigger = trigger;
          this.pointerWasExpanded = trigger?.getAttribute('aria-expanded') === 'true';
        }, options);
        this.addEventListener('pointerover', (event) => {
          if (event.pointerType === 'touch') return;
          if (!event.target.closest('[data-cm26-item]')) return;
          clearTimeout(this.closeTimer);
          const category = event.target.closest('[data-cm26-category]');
          if (category) {
            if (category.contains(event.relatedTarget)) return;
            clearTimeout(this.categoryTimer);
            this.categoryTimer = setTimeout(() => this.selectCategory(category), 120);
          } else {
            const item = event.target.closest('[data-cm26-item]');
            if (item && !item.contains(event.relatedTarget)) this.openItem(item);
          }
        }, options);
        this.addEventListener('pointerout', (event) => {
          const category = event.target.closest('[data-cm26-category]');
          if (category && !category.contains(event.relatedTarget)) clearTimeout(this.categoryTimer);
          const item = event.target.closest('[data-cm26-item]');
          if (item && !item.contains(event.relatedTarget)) {
            this.closeTimer = setTimeout(() => this.reset(), 180);
          }
        }, options);
        this.addEventListener('focusin', (event) => {
          const item = event.target.closest('[data-cm26-item]');
          if (item) this.openItem(item);
          const category = event.target.closest('[data-cm26-category]');
          if (category) this.selectCategory(category);
        }, options);
        this.addEventListener('focusout', () => {
          queueMicrotask(() => { if (!this.contains(document.activeElement)) this.reset(); });
        }, options);
        const positionAnchor = this.closest('#myCoustomDSHeadWrapId') || this;
        this.updatePosition = () => {
          if (!this.activeItem) return;
          // Anchor to the menu row, excluding the header's bottom padding/search area.
          const limit = parseFloat(getComputedStyle(this).getPropertyValue('--cm26-panel-top-limit')) || 156;
          const top = Math.max(0, Math.min(window.innerHeight, limit, positionAnchor.getBoundingClientRect().bottom));
          this.style.setProperty('--cm26-panel-top', `${top}px`);
        };
        window.addEventListener('scroll', this.updatePosition, { ...options, passive: true });
        window.addEventListener('resize', this.updatePosition, options);
        this.resizeObserver = new ResizeObserver(this.updatePosition);
        this.resizeObserver.observe(this.closest('.header') || this, { box: 'border-box' });
        this.resizeObserver.observe(positionAnchor, { box: 'border-box' });
      }
      desktopQuery.addEventListener('change', () => this.reset(), options);
    }

    disconnectedCallback() {
      clearTimeout(this.closeTimer);
      clearTimeout(this.categoryTimer);
      this.stopPanelAnimations();
      this.abort?.abort();
      this.resizeObserver?.disconnect();
      if (openDrawer?.contains(this)) closeDrawer(false);
    }

    controlledPanel(trigger) {
      const panel = document.getElementById(trigger.getAttribute('aria-controls'));
      return panel && this.contains(panel) ? panel : null;
    }

    clearLeavingPanel() {
      if (!this.leavingPanel) return;
      this.leavingPanel.hidden = true;
      this.leavingPanel.inert = false;
      this.leavingPanel.removeAttribute('aria-hidden');
      this.leavingPanel.classList.remove('cm26-panel--leaving');
      this.leavingPanel = null;
    }

    stopPanelAnimations() {
      this.panelAnimations?.forEach((animation) => animation.cancel());
      this.panelAnimations = [];
      this.clearLeavingPanel();
    }

    animatePanel(panel, previousPanel, previousOpacity) {
      const timing = { duration: previousPanel ? 350 : 400, easing: 'cubic-bezier(.2, .7, .2, 1)' };
      this.panelAnimations.push(panel.animate([{ opacity: 0 }, { opacity: 1 }], timing));
      const content = panel.querySelector('.cm26-panel-inner');
      if (content) this.panelAnimations.push(content.animate([
        { transform: 'translateY(-8px)' }, { transform: 'translateY(0)' },
      ], timing));
      if (previousPanel) {
        const fadeOut = previousPanel.animate([{ opacity: previousOpacity }, { opacity: 0 }], { ...timing, duration: 280 });
        fadeOut.onfinish = () => {
          if (this.leavingPanel === previousPanel) this.clearLeavingPanel();
        };
        this.panelAnimations.push(fadeOut);
      }
    }

    reset(preservePanel = null) {
      clearTimeout(this.closeTimer);
      clearTimeout(this.categoryTimer);
      this.stopPanelAnimations();
      this.querySelectorAll('[data-cm26-panel], [data-cm26-category-panel], [data-cm26-mask]').forEach((panel) => {
        if (preservePanel?.contains(panel)) return;
        panel.hidden = true;
        panel.inert = false;
      });
      if (preservePanel) {
        preservePanel.inert = true;
        preservePanel.setAttribute('aria-hidden', 'true');
        preservePanel.classList.add('cm26-panel--leaving');
        this.leavingPanel = preservePanel;
      }
      this.querySelectorAll('[aria-expanded]').forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
      const root = this.querySelector('[data-cm26-root]');
      if (root) root.inert = false;
      this.screenStack = [];
      this.updateScreenHeader();
      this.activeItem = null;
      this.closest('[data-cm26-drawer]')?.removeAttribute('data-cm26-depth');
      this.querySelectorAll('[data-cm26-screen-hidden]').forEach((element) => {
        element.inert = false;
        element.removeAttribute('data-cm26-screen-hidden');
      });
    }

    openItem(item) {
      clearTimeout(this.closeTimer);
      if (this.activeItem === item) return;
      const trigger = item.querySelector('[data-cm26-trigger]');
      const panel = trigger && this.controlledPanel(trigger);
      if (!panel) { this.reset(); return; }
      const animate = !this.mobile && !reducedMotionQuery.matches && typeof panel.animate === 'function';
      const previousPanel = animate ? this.activeItem?.querySelector('[data-cm26-panel]') : null;
      const previousOpacity = previousPanel ? getComputedStyle(previousPanel).opacity : 1;
      this.reset(previousPanel);
      this.activeItem = item;
      trigger.setAttribute('aria-expanded', 'true');
      panel.hidden = false;
      this.updatePosition?.();
      const category = panel.querySelector('[data-cm26-category]');
      if (category) this.selectCategory(category);
      const mask = this.querySelector('[data-cm26-mask]');
      if (mask) mask.hidden = item.classList.contains('cm26-menu-item--text');
      if (animate) this.animatePanel(panel, previousPanel, previousOpacity);
    }

    selectCategory(trigger) {
      const panel = this.controlledPanel(trigger);
      if (!panel || !this.activeItem?.contains(trigger)) return;
      this.activeItem.querySelectorAll('[data-cm26-category]').forEach((button) => button.setAttribute('aria-expanded', String(button === trigger)));
      this.activeItem.querySelectorAll('[data-cm26-category-panel]').forEach((other) => { other.hidden = other !== panel; });
    }

    // Screens are nested in their triggers' list items. Disable siblings, never an ancestor
    // of the active screen, so links behind the screen cannot receive keyboard focus.
    setScreenFocus(panel, disable) {
      let child = panel;
      while (child && child !== this) {
        const parent = child.parentElement;
        if (!parent || parent === this) break;
        Array.from(parent.children).forEach((sibling) => {
          if (sibling === child || sibling.contains(panel)) return;
          if (disable && !sibling.inert) {
            sibling.inert = true;
            sibling.dataset.cm26ScreenHidden = String(this.screenStack.length);
          }
        });
        child = parent;
      }
    }

    updateScreenHeader() {
      const title = this.querySelector('[data-cm26-screen-title]');
      const screen = this.screenStack[this.screenStack.length - 1];
      if (title) title.textContent = screen?.panel.dataset.cm26Title || '';
    }

    pushScreen(trigger, panel) {
      const drawer = this.closest('[data-cm26-drawer]');
      if (drawer && !this.screenStack.length) {
        this.rootScrollTop = drawer.scrollTop;
        drawer.scrollTop = 0;
      }
      trigger.setAttribute('aria-expanded', 'true');
      panel.hidden = false;
      panel.scrollTop = 0;
      this.screenStack.push({ trigger, panel });
      drawer?.setAttribute('data-cm26-depth', String(this.screenStack.length));
      this.updateScreenHeader();
      this.setScreenFocus(panel, true);
      this.querySelector('[data-cm26-back]')?.focus();
    }

    popScreen() {
      const depth = this.screenStack.length;
      const screen = this.screenStack.pop();
      if (!screen) return;
      this.updateScreenHeader();
      screen.panel.hidden = true;
      screen.trigger.setAttribute('aria-expanded', 'false');
      this.querySelectorAll(`[data-cm26-screen-hidden="${depth}"]`).forEach((element) => {
        element.inert = false;
        element.removeAttribute('data-cm26-screen-hidden');
      });
      screen.trigger.focus();
      if (!this.screenStack.length) {
        const drawer = this.closest('[data-cm26-drawer]');
        drawer?.removeAttribute('data-cm26-depth');
        if (drawer) drawer.scrollTop = this.rootScrollTop || 0;
      }
    }

    onClick(event) {
      if (event.target.closest('[data-cm26-close]')) { closeDrawer(); return; }
      if (event.target.closest('[data-cm26-mask]')) { this.reset(); return; }
      if (event.target.closest('[data-cm26-back]')) { this.popScreen(); return; }
      const trigger = event.target.closest('[data-cm26-trigger], [data-cm26-category]');
      if (!trigger) return;
      const panel = this.controlledPanel(trigger);
      if (!panel) return;
      if (!this.mobile) {
        const wasExpanded = this.pointerTrigger === trigger ? this.pointerWasExpanded : trigger.getAttribute('aria-expanded') === 'true';
        this.pointerTrigger = null;
        if (trigger.matches('[data-cm26-category]')) {
          this.selectCategory(trigger);
        } else if (trigger.tagName !== 'A' || (window.matchMedia('(hover: none)').matches && !wasExpanded)) {
          event.preventDefault();
          if (wasExpanded) this.reset();
          else this.openItem(trigger.closest('[data-cm26-item]'));
        }
        return;
      }
      event.preventDefault();
      this.pushScreen(trigger, panel);
    }

    onKeyDown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        if (this.mobile) {
          if (this.screenStack.length) this.popScreen();
          else closeDrawer();
        } else {
          const trigger = this.activeItem?.querySelector('[data-cm26-trigger]');
          // Focus before reset: focusin may otherwise reopen the menu.
          trigger?.focus();
          this.reset();
        }
      } else if (!this.mobile && event.key === 'ArrowDown') {
        const trigger = event.target.closest('[data-cm26-trigger]');
        if (trigger) {
          event.preventDefault();
          this.openItem(trigger.closest('[data-cm26-item]'));
          const panel = this.controlledPanel(trigger);
          visibleFocusables(panel)[0]?.focus();
        }
      }
    }
  }

  customElements.define('custom-menu-202610', CustomMenu202610);
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-cm26-open]');
    if (!button) return;
    // Handle the new drawer before the theme's legacy .mobile-btn click handler.
    event.preventDefault();
    event.stopPropagation();
    showDrawer(button);
  }, true);
  document.addEventListener('click', (event) => {
    document.querySelectorAll('custom-menu-202610[data-mode="desktop"]').forEach((menu) => {
      if (!menu.contains(event.target)) menu.reset();
    });
  });
  document.addEventListener('keydown', (event) => {
    if (!openDrawer) return;
    if (event.key === 'Escape') { event.preventDefault(); closeDrawer(); }
    if (event.key !== 'Tab') return;
    const focusables = visibleFocusables(openDrawer);
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (event.shiftKey && (document.activeElement === first || !openDrawer.contains(document.activeElement))) {
      event.preventDefault(); last?.focus();
    } else if (!event.shiftKey && (document.activeElement === last || !openDrawer.contains(document.activeElement))) {
      event.preventDefault(); first?.focus();
    }
  });
  desktopQuery.addEventListener('change', () => closeDrawer(false));
  document.addEventListener('shopify:section:unload', (event) => {
    if (openDrawer && event.target.contains(openDrawer)) closeDrawer(false);
  });
  // Keep the legacy public hooks available to other theme integrations.
  window.switchbotMenuMbBtnOpenIdClickFn = () => { showDrawer(document.querySelector('[data-cm26-open]')); return false; };
  window.switchbotMenuMbBtnCloseIdClickFn = () => { closeDrawer(); return false; };
})();
